import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Input,
  VStack,
  FormControl,
  FormLabel,
  HStack,
  useToast,
  List,
  ListItem,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { InfluencerLocation } from '@foodfluence/shared';
import {
  createCompleteLocation,
  googleMapsResultToLocation,
  defaultInfluencerLocation,
} from '../../../utils/locationUtils';
import { geocodePostalCode, getCountrySuggestions } from '../../../services/api/location';
import { ff } from '../../../theme';

interface LocationFormProps {
  onCancel: () => void;
  onSave: (location: InfluencerLocation, locationIndex?: number) => void;
  locationData?: InfluencerLocation;
  locationIndex?: number;
  isEditing?: boolean;
}

const LocationForm = ({
  onCancel,
  onSave,
  locationData,
  locationIndex,
  isEditing = false,
}: LocationFormProps) => {
  const toast = useToast();

  const [formData, setFormData] = useState<{
    postalCode: string;
    country: string;
  }>({
    postalCode: locationData?.zipCode ?? '',
    country: locationData?.country || defaultInfluencerLocation.country,
  });

  const [isGeocoding, setIsGeocoding] = useState(false);
  const [countrySuggestions, setCountrySuggestions] = useState<string[]>([]);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);

  const fetchCountrySuggestions = useCallback(async (input: string) => {
    if (input.trim().length < 2) {
      setCountrySuggestions([]);
      return;
    }
    try {
      const results = await getCountrySuggestions(input.trim());
      console.log('📍 Country suggestions:', results);
      setCountrySuggestions(results);
      setShowCountrySuggestions(true);
    } catch (error) {
      console.error('Error fetching country suggestions:', error);
      setCountrySuggestions([]);
    }
  }, []);

  const debouncedFetchCountrySuggestions = useCallback(debounce(fetchCountrySuggestions, 300), [fetchCountrySuggestions]);

  const handleCountryChange = (value: string) => {
    handleInputChange('country', value);
    debouncedFetchCountrySuggestions(value);
  };

  const handleCountrySuggestionClick = (suggestion: string) => {
    setFormData((prev) => ({ ...prev, country: suggestion }));
    setCountrySuggestions([]);
    setShowCountrySuggestions(false);
  };

  // Initialize form data when component mounts
  useEffect(() => {
    if (locationData) {
      setFormData({
        postalCode: locationData.zipCode || '',
        country: locationData.country || defaultInfluencerLocation.country,
      });
    } else {
      setFormData({
        postalCode: '',
        country: defaultInfluencerLocation.country,
      });
    }
  }, [locationData]);

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.postalCode.trim()) {
      toast({
        title: 'Postal Code Required',
        description: 'Please enter a postal code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.country.trim()) {
      toast({
        title: 'Country Required',
        description: 'Please enter a country',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsGeocoding(true);
    try {
      // Geocode using structured postal code query for better accuracy
      console.log('🔍 Geocoding postal code:', formData.postalCode, 'in country:', formData.country);
      
      const results = await geocodePostalCode(formData.postalCode, formData.country);
      console.log('📍 Geocode results:', results);
      
      if (!results || results.length === 0) {
        toast({ 
          title: 'Location Not Found', 
          description: 'Could not find location for this postal code and country',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Take first result and convert to InfluencerLocation
      const result = results[0];
      console.log('✅ Using first result:', {
        formattedAddress: result.formattedAddress,
        city: result.city,
        state: result.state,
        country: result.country,
        lat: result.lat,
        lng: result.lng
      });
      
      const baseLocation = createCompleteLocation(locationData);
      const newLocation = googleMapsResultToLocation(result, baseLocation);
      console.log('💾 Final location to save:', newLocation);

      // Call the onSave callback — parent is responsible for hiding the form
      onSave(newLocation, locationIndex);
      
    } catch (error) {
      console.error('Geocoding error:', error);
      toast({
        title: "Couldn't find this address",
        description: "Please check the postal code and country, then try again.",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsGeocoding(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Postal Code */}
      <FormControl>
        <FormLabel>
          Postal code
        </FormLabel>
        <Input
          placeholder="Enter postal code"
          value={formData.postalCode}
          onChange={(e) => handleInputChange('postalCode', e.target.value)}
        />
      </FormControl>

      {/* Country */}
      <FormControl position="relative">
        <FormLabel>
          Country
        </FormLabel>
        <Input
          placeholder="Enter country"
          value={formData.country}
          onChange={(e) => handleCountryChange(e.target.value)}
          onBlur={() => setTimeout(() => setShowCountrySuggestions(false), 200)}
        />
        {showCountrySuggestions && countrySuggestions.length > 0 && (
          <List
            position="absolute"
            top="100%"
            left={0}
            right={0}
            bg="white"
            borderRadius="14px"
            maxH="200px"
            overflowY="auto"
            zIndex={1000}
            mt={1}
            border="1px solid"
            borderColor={ff.border}
          >
            {countrySuggestions.map((suggestion, index) => (
              <ListItem
                key={`${suggestion}-${index}`}
                px={4}
                py={3}
                cursor="pointer"
                _hover={{ bg: ff.grey }}
                onClick={() => handleCountrySuggestionClick(suggestion)}
                fontSize="14px"
                color={ff.ink}
              >
                {suggestion}
              </ListItem>
            ))}
          </List>
        )}
      </FormControl>

      {/* Buttons */}
      <HStack spacing={3} pt={1}>
        <Button
          flex={1}
          variant="inkOutline"
          onClick={onCancel}
          h="44px"
          isDisabled={isGeocoding}
        >
          Cancel
        </Button>
        <Button
          flex={1}
          variant="lime"
          onClick={handleSave}
          h="44px"
          isLoading={isGeocoding}
          loadingText="Finding..."
        >
          {isEditing ? 'Save' : 'Add'}
        </Button>
      </HStack>
    </VStack>
  );
};

export default LocationForm;